import React from "react";
import Wordmark from "./Wordmark";

// Concise, HONEST public-beta trust surfaces. These describe the product's
// ACTUAL current behavior — no invented certifications, encryption claims,
// deletion guarantees or "we never store X" statements the code can't back up.
// Future features are clearly labelled as future.

function Shell({ title, children, onBack }) {
  return (
    <div className="relative min-h-[100dvh] w-full max-w-[680px] mx-auto px-6 py-10">
      <div className="flex items-center justify-between">
        <button
          type="button"
          data-testid="legal-back"
          onClick={onBack}
          className="font-body text-[11px] tracking-[0.18em] uppercase text-silver hover:text-bone transition-colors"
        >
          ← Back
        </button>
        <Wordmark variant="horizontal" className="h-3.5" />
      </div>

      <h1 className="mt-10 font-display font-black uppercase tracking-[-0.01em] text-bone text-3xl">
        {title}
      </h1>
      <div className="mt-6 space-y-5 font-body text-[13.5px] leading-relaxed text-silver">
        {children}
      </div>
      <p className="mt-10 font-mono text-[10px] tracking-[0.24em] uppercase text-subtle">
        Public beta · last updated 2025
      </p>
    </div>
  );
}

function H({ children }) {
  return (
    <h2 className="font-display font-bold uppercase tracking-[0.06em] text-bone text-sm mt-7 mb-1">
      {children}
    </h2>
  );
}

export function PrivacyPage({ onBack }) {
  return (
    <Shell title="Privacy" onBack={onBack}>
      <p>
        TASTE TEST is a public beta. This note describes, in plain language, what
        the product actually does with data today. We only claim what we do.
      </p>

      <H>No account required</H>
      <p>
        You can play without signing up. We do not ask for your name, email or
        phone number, and there is no login. When you first play, your browser
        stores a random guest identifier (for example{" "}
        <span className="text-bone/80">guest_ab12cd34</span>) so a single play
        session holds together. It is not linked to your real-world identity by
        us.
      </p>

      <H>What we store</H>
      <p>
        To build and show your Taste Profile we store, against that guest
        identifier: the KEEP ONE choices you make, which items you marked as not
        seen/played, basic timing of choices, the derived taste profile, and — if
        you create one — the share or challenge link tokens. We also record
        we can tell whether the product works. This includes your browser’s
        user-agent string.
      </p>

      <H>What we do NOT do</H>
      <p>
        We do not sell your data. We do not run third-party advertising or
        cross-site tracking pixels. We do not connect to Letterboxd, Steam,
        Spotify, IMDb or any external account (that is a possible future,
        opt-in-only feature — it is not active today). We do not attempt to infer
        sensitive attributes about you.
      </p>

      <H>Sharing</H>
      <p>
        A Face-Off or Challenge link only carries the specific matchup or
        challenge context needed for a friend to play. Your full private Taste
        Profile is not made public and is not addressable by a shared link.
      </p>

      <H>Your control</H>
      <p>
        Because play is anonymous, clearing your browser’s site data removes the
        local guest identifier on your device. If you would like server-side data
        associated with a specific guest identifier removed during the beta,
        contact us and we will help.
      </p>

      <H>Changes</H>
      <p>
        As the beta evolves, this page will be updated to keep describing the
        actual behavior of the product.
      </p>
    </Shell>
  );
}

export function TermsPage({ onBack }) {
  return (
    <Shell title="Terms" onBack={onBack}>
      <p>
        These are concise beta terms for TASTE TEST. By playing, you agree to
        them.
      </p>

      <H>Beta software</H>
      <p>
        TASTE TEST is provided as a public beta, “as is”, for personal
        entertainment. Features, content and results may change, break or be
        reset while we improve the product.
      </p>

      <H>What it is</H>
      <p>
        TASTE TEST is a game about your taste in culture. It shows two works, you
        KEEP ONE, and it builds a taste profile from your choices. Titles, years
        and related metadata are cultural references used for identification and
        commentary. Trademarks and works referenced belong to their respective
        owners; their appearance here does not imply any endorsement or
        affiliation.
      </p>

      <H>Fair use of the service</H>
      <p>
        Please use TASTE TEST for its intended purpose. Do not attempt to disrupt
        the service, abuse shared links, or misuse it in a way that harms other
        players or the product.
      </p>

      <H>No guarantees</H>
      <p>
        Taste profiles and matches are for fun and reflection, not professional
        advice. We do not guarantee uninterrupted availability during the beta.
      </p>

      <H>Contact</H>
      <p>
        Questions about these terms or your data can be sent to us and we will do
        our best to help during the beta.
      </p>
    </Shell>
  );
}
